import { Components, Theme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'

/**
 * VERA-842: the app's base Alert.
 *
 * Previously this override set `display: 'none'` on the root, so EVERY MUI
 * Alert in the app rendered invisible (that, not the shadow DOM, is why the
 * shared `@repo/ui` Alert appeared broken here). It also painted
 * `standardSuccess` with `background: 'red'`, and only the error severity had a
 * border colour even though every severity was given a 1px solid border.
 *
 * Now: each severity's border matches ITS OWN status colour, with a soft tint
 * of the same colour behind it, so an alert reads as one coherent status block.
 */
const severityStyles = (theme: Theme, color: string) => ({
  // `!important` is what makes this hold, NOT the fact that it is its own
  // property. index.css sets `#vera-shadow-root * { border-color: … }`, whose
  // ID specificity beats any emotion class rule regardless of how the colour is
  // written — that is what rendered alert borders grey next to coloured text.
  // Only `!important` outranks it. (An earlier comment here blamed Tailwind
  // preflight; preflight is disabled in tailwind.config.js.)
  borderStyle: 'solid',
  borderWidth: '1px',
  // Subtle: the severity colour at low alpha, not full strength.
  borderColor: `${alpha(color, 0.28)} !important`,
  backgroundColor: alpha(color, 0.06),
  color: theme.palette.text.primary,
  '.MuiAlert-icon': { color },
})

/**
 * `info` is neutral-TINTED — a slight zinc wash, not a white box and not MUI blue.
 *
 * This lands between two earlier positions, and the distinction is the whole point:
 *
 *  1. Originally it took MUI's STOCK BLUE, because apps/app never customises
 *     `theme.palette.info` in palette.ts. That blue is an accent with no meaning
 *     anywhere else in this app, so it read as alien — correctly rejected.
 *  2. It was then made fully WHITE/paper. That removed the blue but also removed
 *     the surface: an info note became a plain bordered box, indistinguishable
 *     from any other bordered box on the page.
 *
 * Position 2 over-corrected. `@repo/ui`'s Alert — the one apps/platform renders —
 * does tint its info variant, just not with blue: `TINT_HUE.info` is the zinc
 * `#71717a`, laid down at 6% (`alpha(hue, 0.06)` in light mode). Zinc is a
 * NEUTRAL, so it carries none of blue's false "status" signal while still giving
 * the note its own surface. Matching that value here is what makes an info alert
 * look the same in the app as it does on platform, which is the actual ask.
 *
 * Keep this in step with `packages/ui/.../Alert/Alert.tsx`'s `TINT_HUE.info` /
 * `TINT_TEXT.info`; the constants are not exported, so this is a mirrored literal.
 */
const INFO_TINT_HUE = '#71717a'

const infoStyles = (theme: Theme) => ({
  borderStyle: 'solid',
  borderWidth: '1px',
  // Same 0.28 border / 0.06 fill relationship the coloured severities use above,
  // so info sits in the same family rather than being a special case.
  borderColor: `${alpha(INFO_TINT_HUE, 0.28)} !important`,
  backgroundColor: alpha(INFO_TINT_HUE, 0.06),
  color: theme.palette.text.primary,
  '.MuiAlert-icon': { color: INFO_TINT_HUE },
})

export const MuiAlert: Components<Theme>['MuiAlert'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      // VERA-304: a fixed 6px, NOT the app's shape token. The alert's corner
      // should be stable regardless of what an individual company's Studio
      // theme sets for `theme.shape.borderRadius` — matches @repo/ui's Alert
      // (`StyledMuiAlert`'s `borderRadius: 6`) so both alert paths agree.
      borderRadius: '6px',
      borderWidth: '1px',
      borderStyle: 'solid',
      '.MuiAlert-message': { padding: 0 },
      '.MuiAlert-icon': { padding: 0, marginRight: 0 },
      '.MuiAlertTitle-root': { margin: 0 },

      // Border + tint follow the severity, for both `standard` and `outlined`.
      '&.MuiAlert-standardError, &.MuiAlert-outlinedError': severityStyles(
        theme,
        theme.palette.error.main,
      ),
      '&.MuiAlert-standardWarning, &.MuiAlert-outlinedWarning': severityStyles(
        theme,
        theme.palette.warning.main,
      ),
      // info is neutral (see `infoStyles` above) — not `severityStyles(theme, theme.palette.info.main)`.
      '&.MuiAlert-standardInfo, &.MuiAlert-outlinedInfo': infoStyles(theme),
      '&.MuiAlert-standardSuccess, &.MuiAlert-outlinedSuccess': severityStyles(
        theme,
        theme.palette.success.main,
      ),
    }),
  },
}
